/**
 * ChangeCompanyDialog — reusable company picker dialog.
 *
 * Replaces the browser-native prompt()/confirm()/alert() chain that was
 * previously the only way to reassign a contact to a different company.
 * Shows a searchable, keyboard-navigable list of all companies (name +
 * category + headquarters) so the caller can disambiguate when multiple
 * companies share a prefix. Filters out the contact's current company so
 * the list only shows meaningful destinations.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ChangeCompanyDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     currentCompanyId={contact.companyId}
 *     title="Move contact to another company"
 *     onSelect={(company) => { ... patch contact, toast, etc. ... }}
 *   />
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Building2, Check } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Loader2 } from "lucide-react";

export interface PickableCompany {
  id: string;
  name: string;
  category: string;
  headquarters?: string | null;
}

interface ChangeCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hides this company from the picker (the caller's current affiliation). */
  currentCompanyId?: string | null;
  /** Callback fired when the user picks a destination company. */
  onSelect: (company: PickableCompany) => void | Promise<void>;
  title?: string;
  description?: string;
}

export function ChangeCompanyDialog({
  open,
  onOpenChange,
  currentCompanyId,
  onSelect,
  title = "Select a company",
  description = "Search by name. The contact will be reassigned to the company you pick.",
}: ChangeCompanyDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  // Fetch the full company list once per dialog-open. Matches the pattern
  // AddContactDrawer uses, which has worked at the current dataset size.
  // If the company count ever grows past the point where client-side
  // filtering is too slow, switch to server-side /api/search with a
  // debounced query here.
  const { data: companies = [], isLoading } = useQuery<PickableCompany[]>({
    queryKey: ["companies-picker"],
    queryFn: async () => {
      const res = await fetch("/api/companies", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load companies");
      const data = await res.json();
      return (Array.isArray(data) ? data : []).map((c: any) => ({
        id: c.id,
        name: c.name,
        category: c.category,
        headquarters: c.headquarters,
      }));
    },
    enabled: open,
    staleTime: 60_000,
  });

  const candidates = companies.filter(c => c.id !== currentCompanyId);

  const handlePick = async (c: PickableCompany) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSelect(c);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Command className="border-0">
          <CommandInput placeholder="Type to search companies..." autoFocus />
          <CommandList className="max-h-[400px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading companies...
              </div>
            ) : (
              <>
                <CommandEmpty>No companies match your search.</CommandEmpty>
                {candidates.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`${c.name} ${c.category} ${c.headquarters || ""}`}
                    onSelect={() => handlePick(c)}
                    className="px-4 py-3 cursor-pointer"
                    disabled={submitting}
                  >
                    <Building2 className="w-4 h-4 mr-3 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.category}
                        {c.headquarters ? ` · ${c.headquarters}` : ""}
                      </div>
                    </div>
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin ml-2" />
                    ) : (
                      <Check className="w-4 h-4 ml-2 opacity-0 group-data-[selected=true]:opacity-100" />
                    )}
                  </CommandItem>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
